import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type { ImageFile } from "../App";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Slider } from "@/components/ui/slider";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { X, Save, Palette, Image as ImageIcon } from "lucide-react";

interface EditModalProps {
  image: ImageFile;
  isOpen: boolean;
  onClose: () => void;
  onSave: (url: string) => void;
}

const predefinedColors = [
  '#ffffff', '#000000', '#F44336', '#4CAF50', '#2196F3',
  '#FFEB3B', '#00BCD4', '#E91E63', '#9E9E9E', '#607D8B'
];

export function EditModal({ image, isOpen, onClose, onSave }: EditModalProps) {
  const { t } = useTranslation();
  const [bgType, setBgType] = useState('color');
  const [bgColor, setBgColor] = useState('#ffffff');
  const [customBgImage, setCustomBgImage] = useState<File | null>(null);
  const [blurValue, setBlurValue] = useState(0);
  const [exportUrl, setExportUrl] = useState('');

  const processedURL = image.processedFile ? URL.createObjectURL(image.processedFile) : '';

  useEffect(() => {
    if (image.processedFile && isOpen) {
      applyChanges();
    }
  }, [bgType, bgColor, customBgImage, blurValue, isOpen]);

  const applyChanges = async () => {
    if (!image.processedFile) return;

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const img = new Image();
    img.src = processedURL;
    await new Promise(resolve => img.onload = resolve);

    canvas.width = img.width;
    canvas.height = img.height;

    // Background
    if (bgType === 'color') {
      ctx.fillStyle = bgColor;
      ctx.fillRect(0, 0, canvas.width, canvas.height);
    } else if (bgType === 'image' && customBgImage) {
      const bgImg = new Image();
      bgImg.src = URL.createObjectURL(customBgImage);
      await new Promise(resolve => bgImg.onload = resolve);
      ctx.drawImage(bgImg, 0, 0, canvas.width, canvas.height);
    }

    if (blurValue > 0) {
      const tempCanvas = document.createElement('canvas');
      tempCanvas.width = canvas.width;
      tempCanvas.height = canvas.height;
      const tempCtx = tempCanvas.getContext('2d');
      if (tempCtx) {
        tempCtx.filter = `blur(${blurValue / 5}px)`;
        tempCtx.drawImage(img, 0, 0);
        ctx.drawImage(tempCanvas, 0, 0);
      } else {
        ctx.drawImage(img, 0, 0);
      }
    } else {
      ctx.drawImage(img, 0, 0);
    }

    setExportUrl(canvas.toDataURL('image/png'));
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-6xl w-[95vw] h-[85vh] p-0 gap-0 overflow-hidden border-none bg-background shadow-2xl flex flex-col md:rounded-[2rem]">
        {/* Header */}
        <div className="flex items-center justify-between px-8 py-6 border-b shrink-0">
          <div className="space-y-1">
            <DialogTitle className="text-xl font-black tracking-tight uppercase italic">{t('app.title')} {t('app.subtitle')}</DialogTitle>
            <DialogDescription className="text-[10px] text-muted-foreground font-bold tracking-[0.2em] uppercase">Advanced Local Processing</DialogDescription>
          </div>
          <Button variant="outline" size="icon" onClick={onClose} className="h-10 w-10 rounded-full border-muted-foreground/20">
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Main Content Area */}
        <div className="flex-1 grid grid-cols-1 md:grid-cols-12 overflow-hidden">
          {/* Controls - Sidebar Style */}
          <div className="md:col-span-4 lg:col-span-3 p-8 space-y-8 border-r bg-muted/5 overflow-y-auto">
            <div className="space-y-6">
              <Tabs value={bgType} onValueChange={setBgType} className="w-full">
                <div className="flex items-center justify-between mb-4">
                  <span className="text-[11px] font-black uppercase tracking-[0.2em] text-primary flex items-center gap-2">
                    <Palette className="h-4 w-4" /> {t('edit_modal.canvas')}
                  </span>
                </div>
                <TabsList className="grid grid-cols-2 w-full h-11 bg-muted/50 p-1 rounded-xl">
                  <TabsTrigger value="color" className="text-xs font-bold rounded-lg data-[state=active]:bg-background">{t('controls.color')}</TabsTrigger>
                  <TabsTrigger value="image" className="text-xs font-bold rounded-lg data-[state=active]:bg-background">{t('controls.scene')}</TabsTrigger>
                </TabsList>

                <TabsContent value="color" className="pt-6 space-y-6">
                  <div className="grid grid-cols-5 gap-4">
                    {predefinedColors.map(color => (
                      <button
                        key={color}
                        onClick={() => setBgColor(color)}
                        className={`aspect-square w-full rounded-2xl border-2 transition-colors duration-150 shadow-sm ${bgColor === color ? 'border-primary ring-4 ring-primary/10' : 'border-transparent hover:border-primary/30'}`}
                        style={{ backgroundColor: color }}
                      />
                    ))}
                  </div>

                  <div className="flex items-center gap-4 p-5 bg-background rounded-[1.5rem] border shadow-sm group transition-all hover:border-primary/50">
                    <div className="relative h-12 w-12 shrink-0 overflow-hidden rounded-xl border-2 border-muted shadow-inner">
                      <input
                        type="color"
                        value={bgColor}
                        onChange={(e) => setBgColor(e.target.value)}
                        className="absolute -inset-4 h-20 w-20 cursor-pointer"
                      />
                    </div>
                    <div className="space-y-1">
                      <p className="text-xs font-black uppercase tracking-wider">{t('controls.custom_color')}</p>
                      <code className="text-[11px] text-muted-foreground font-mono bg-muted/50 px-2 py-0.5 rounded">{bgColor}</code>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="image" className="pt-6">
                  <div className="relative group border-2 border-dashed rounded-[1.5rem] p-8 transition-all hover:border-primary/50 hover:bg-primary/5 text-center space-y-3 cursor-pointer">
                    <div className="h-12 w-12 bg-primary/10 rounded-2xl flex items-center justify-center mx-auto transition-transform group-hover:scale-110 group-hover:bg-primary group-hover:text-white">
                      <ImageIcon className="h-6 w-6" />
                    </div>
                    <div>
                      <p className="text-xs font-bold">{t('controls.upload_scene')}</p>
                      <p className="text-[10px] text-muted-foreground">{t('controls.supported_formats')}</p>
                    </div>
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={(e) => setCustomBgImage(e.target.files?.[0] || null)}
                      className="absolute inset-0 opacity-0 cursor-pointer h-full w-full"
                    />
                  </div>
                </TabsContent>
              </Tabs>
            </div>

            <div className="space-y-4 pt-4 border-t">
              <div className="flex justify-between items-center px-1">
                <span className="text-[11px] font-black uppercase tracking-[0.2em] text-muted-foreground">{t('controls.blur')}</span>
                <span className="text-xs tabular-nums font-black text-primary bg-primary/10 px-3 py-1 rounded-full">{blurValue}%</span>
              </div>
              <Slider
                value={[blurValue]}
                onValueChange={(v: number[]) => setBlurValue(v[0])}
                max={100}
                step={1}
                className="py-2"
              />
            </div>
          </div>

          {/* Preview Viewport */}
          <div className="md:col-span-8 lg:col-span-9 p-8 md:p-12 bg-[#F8F9FB] dark:bg-black/20 flex items-center justify-center relative">
            <div className="relative w-full h-full flex items-center justify-center">
              <div className="absolute inset-0 bg-checkered opacity-[0.03] pointer-events-none" />

              <div className="relative max-h-full max-w-full rounded-[2.5rem] overflow-hidden shadow-2xl bg-white border p-4 bg-checkered bg-opacity-5">
                <img
                  draggable="false"
                  src={exportUrl || processedURL}
                  alt="Preview"
                  className="relative max-h-[calc(70vh-100px)] object-contain"
                />

                <div className="absolute top-8 left-8">
                  <div className="bg-black/80 text-white text-[9px] font-black uppercase tracking-[0.3em] px-4 py-2 rounded-full backdrop-blur-md border border-white/10">
                    {t('edit_modal.render_output')}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Action Footer */}
        <div className="px-10 py-6 border-t flex items-center justify-between bg-muted/5 shrink-0">
          <div className="hidden md:flex items-center gap-4">
            <div className="h-2 w-2 bg-green-500 rounded-full animate-pulse" />
            <span className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest">Real-time Preview Active</span>
          </div>
          <div className="flex gap-4 w-full md:w-auto">
            <Button variant="ghost" onClick={onClose} className="px-8 font-black text-[11px] uppercase tracking-widest h-12 rounded-2xl hover:bg-destructive/10 hover:text-destructive transition-all">{t('edit_modal.cancel')}</Button>
            <Button onClick={() => { onSave(exportUrl); onClose(); }} className="flex-1 md:flex-none px-12 font-black text-[11px] uppercase tracking-widest h-12 rounded-2xl shadow-2xl shadow-primary/30 transition-all hover:scale-[1.02] active:scale-[0.98]">
              <Save className="h-4 w-4 mr-3" /> {t('edit_modal.save_changes')}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
